import React, { useState, useEffect, useRef } from "react";

export default function TraceWebsiteRequest() {
  const correctOrder = [
    "Type URL",
    "DNS converts URL to IP",
    "Request sent via Internet",
    "Server prepares webpage",
    "Response sent back via Internet",
    "Browser displays page",
  ];

  const hints = [
    "I am what you type to start browsing!",
    "I turn words into numbers so servers can find me.",
    "I carry your message across the world.",
    "I read the request and prepare what you want to see.",
    "I send the webpage back via the network.",
    "I show the page on your screen!",
  ];

  const [shuffledCards, setShuffledCards] = useState([]);
  const [draggedCard, setDraggedCard] = useState(null);
  const [dropStates, setDropStates] = useState(Array(correctOrder.length).fill(""));
  const [currentHint, setCurrentHint] = useState(0);
  const [success, setSuccess] = useState(false);

  // Sound refs
  const correctSound = useRef(null);
  const wrongSound = useRef(null);
  const successSound = useRef(null);

  useEffect(() => {
    correctSound.current = new Audio("https://www.soundjay.com/buttons/sounds/button-3.mp3");
    wrongSound.current = new Audio("https://www.soundjay.com/buttons/sounds/button-10.mp3");
    successSound.current = new Audio("https://www.soundjay.com/misc/sounds/bell-ringing-05.mp3");
  }, []);

  // Shuffle cards at start
  useEffect(() => {
    const shuffle = (array) => {
      let arr = [...array];
      let currentIndex = arr.length, randomIndex;
      while (currentIndex !== 0) {
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex--;
        [arr[currentIndex], arr[randomIndex]] = [arr[randomIndex], arr[currentIndex]];
      }
      return arr;
    };
    setShuffledCards(shuffle(correctOrder));
  }, []);

  const handleDragStart = (card) => {
    setDraggedCard(card);
  };

  const handleDrop = (index) => {
    if (!draggedCard) return;

    if (draggedCard === correctOrder[index]) {
      // Play correct sound
      correctSound.current.play();

      const newDropStates = [...dropStates];
      newDropStates[index] = draggedCard;
      setDropStates(newDropStates);

      // Move to next hint
      if (currentHint < hints.length - 1) {
        setCurrentHint(currentHint + 1);
      }

      // Remove card from shuffledCards
      setShuffledCards((prev) => prev.filter((c) => c !== draggedCard));

      // Check success
      if (newDropStates.every((c) => c !== "")) {
        setSuccess(true);
        successSound.current.play();
      }
    } else {
      // Play wrong sound
      wrongSound.current.play();
    }
    setDraggedCard(null);
  };

  const cardStyle = [
    { background: "#f44336" },
    { background: "#ff9800" },
    { background: "#4caf50" },
    { background: "#2196f3" },
    { background: "#9c27b0" },
    { background: "#ff5722" },
  ];

  return (
    <div
      style={{
        fontFamily: "Comic Sans MS, cursive",
        background: "linear-gradient(to bottom,rgb(134, 224, 236),rgb(131, 209, 240))",
        minHeight: "100vh",
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <h3 style={{ color: "#333", textShadow: "1px 1px #fff" }}>Trace the Website Request!</h3>

      {/* Hint */}
      <div
        style={{
          background: "#ffeb3b",
          color: "#333",
          padding: "10px",
          borderRadius: "12px",
          boxShadow: "2px 2px 10px rgba(0,0,0,0.2)",
          marginBottom: "20px",
          minWidth: "300px",
          textAlign: "center",
          fontWeight: "bold",
        }}
      >
        Hint: {hints[currentHint]}
      </div>

      {/* Cards */}
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "15px", marginTop: "20px" }}>
        {shuffledCards.map((card, idx) => (
          <div
            key={card}
            draggable
            onDragStart={() => handleDragStart(card)}
            style={{
              padding: "15px",
              borderRadius: "12px",
              width: 220,
              textAlign: "center",
              fontWeight: "bold",
              cursor: "grab",
              userSelect: "none",
              boxShadow: "2px 2px 8px rgba(0,0,0,0.2)",
              color: "white",
              ...cardStyle[idx % cardStyle.length],
            }}
          >
            {card}
          </div>
        ))}
      </div>

      {/* Dropzones */}
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "15px", marginTop: "30px" }}>
        {correctOrder.map((_, idx) => (
          <div
            key={idx}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop(idx)}
            style={{
              width: 240,
              minHeight: 60,
              border: "3px dashed #333",
              borderRadius: "12px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontStyle: "italic",
              color: "#666",
              background: dropStates[idx] ? "#e8f5e9" : "#f9f9f9",
              fontWeight: "bold",
            }}
          >
            {dropStates[idx] || `Step ${idx + 1}`}
          </div>
        ))}
      </div>

      {success && (
        <div
          style={{
            marginTop: 20,
            background: "#4caf50",
            color: "white",
            padding: "15px 30px",
            borderRadius: "12px",
            fontSize: 18,
          }}
        >
          🎉 You successfully traced the web request!
        </div>
      )}
    </div>
  );
}
